import React, { Suspense, useMemo, useState } from 'react';
import { useStore } from 'react-redux';
import * as Sentry from '@sentry/react';
import { Alert, Spin } from 'antd';

import {
  EmbeddedBuilderEntity,
  EmbeddedBuilderMode,
  IBuilderSaveResult
} from 'components/EmbeddedBuilder';
import { DarkPageScope } from 'components/theme/DarkPageScope';
import { CampaignEntityContext } from 'containers/Campaign/hooks/useCampaignId';
import useMultiLanguageTranslation from 'hooks/useMultiLanguageTranslation';

import { TOrbit } from '../i18n/TModules';
import { BuilderRegistry, builderRegistry } from './builderRegistry';
import { isBuilderBlockedBySingleton } from './singletonGuard';

export interface IOrbitBuilderRequest {
  entity: EmbeddedBuilderEntity;
  mode: EmbeddedBuilderMode;
  /** Required in edit mode. */
  entityId?: number;
  requestId?: number;
  initialStep?: string;
  prefill?: Record<string, unknown>;
  lockedCustomAttributes?: string[];
}

export interface IOrbitBuilderHostProps extends IOrbitBuilderRequest {
  onSaved: (result: IBuilderSaveResult) => void;
  onCancel: () => void;
  /** Overrides the app registry; for tests and previews. */
  registry?: BuilderRegistry;
}

export const ORBIT_BUILDER_HOST_TEST_ID = 'orbit-builder-host';

const OrbitBuilderHost: React.FC<IOrbitBuilderHostProps> = ({
  entity,
  mode,
  entityId,
  requestId,
  initialStep,
  prefill,
  lockedCustomAttributes,
  onSaved,
  onCancel,
  registry = builderRegistry
}) => {
  const { t } = useMultiLanguageTranslation();
  const store = useStore();
  // Read once: the builder registers the very form this checks for as soon as it mounts.
  const [blocked] = useState(() => isBuilderBlockedBySingleton(store.getState(), entity));
  const entry = registry[entity];
  const supported =
    Boolean(entry?.modes.includes(mode)) && (mode !== 'edit' || entityId !== undefined);
  const Builder = useMemo(() => (entry ? React.lazy(entry.load) : null), [entry]);

  const notice = (message: string) => (
    <Alert type="warning" showIcon message={message} data-testid="orbit-builder-notice" />
  );

  let body: React.ReactNode;
  if (!supported || !Builder) {
    body = notice(t(`${TOrbit}.builder.unavailable`));
  } else if (blocked) {
    body = notice(
      t(`${TOrbit}.builder.blocked`, { module: t(`${TOrbit}.builder.module.${entity}`) })
    );
  } else {
    body = (
      <Sentry.ErrorBoundary fallback={notice(t(`${TOrbit}.builder.failed`))}>
        <Suspense fallback={<Spin data-testid="orbit-builder-loading" />}>
          <CampaignEntityContext.Provider
            value={entity === 'campaign' && entityId !== undefined ? String(entityId) : undefined}
          >
            <Builder
              entityId={entityId}
              requestId={requestId}
              initialStep={initialStep ?? entry?.defaultStep}
              prefill={prefill}
              lockedCustomAttributes={lockedCustomAttributes}
              onSaved={onSaved}
              onCancel={onCancel}
            />
          </CampaignEntityContext.Provider>
        </Suspense>
      </Sentry.ErrorBoundary>
    );
  }

  return (
    <DarkPageScope variant="orbit-builder">
      <div data-testid={ORBIT_BUILDER_HOST_TEST_ID} data-entity={entity} data-mode={mode}>
        {body}
      </div>
    </DarkPageScope>
  );
};

export default OrbitBuilderHost;
